// src/pages/AdminPage/AdminPage.js
import React, { useState, useEffect } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  Routes,
  Route,
} from "react-router-dom";
import {
  getStats,
  getRecentActivities,
  getUser,
} from "../../services/apiService";
import PersonnelList from "../../components/PersonnelList/PersonnelList";
import ReportsContent from "../../components/ReportsContent/ReportsContent";
import SettingsContent from "../../components/SettingsContent/SettingsContent";
import PermissionsContent from "../../components/PermissionsContent/PermissionsContent";
import WorkScheduleContent from "../../components/WorkScheduleContent/WorkScheduleContent";
import ManualAttendanceEntry from "../../components/ManualAttendanceEntry/ManualAttendanceEntry";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import Header from "../../components/Header/Header";
import MaintenancePanelSwitcher from "../../components/MaintenancePanelSwitcher/MaintenancePanelSwitcher";
import "./AdminPage.css";

const AdminPage = ({ inMaintenance = false }) => {
  const [stats, setStats] = useState({
    users: 0,
    activeUsers: 0,
    todayEntries: 0,
    todayExits: 0,
    currentlyInOffice: 0,
  });
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState("");
  const [recentLogs, setRecentLogs] = useState([]);
  const [activeView, setActiveView] = useState("dashboard");
  const [error, setError] = useState(null);
  const [refreshPersonnel, setRefreshPersonnel] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = getUser();

  // URL'den activeView'i belirle (sadece maintenance modunda değilken)
  useEffect(() => {
    if (!inMaintenance) {
      const path = location.pathname.split("/").pop();
      if (
        [
          "dashboard",
          "personnel",
          "settings",
          "permissions",
          "work-schedule",
          "attendance",
          "reports",
        ].includes(path)
      ) {
        setActiveView(path);
      }
    }
  }, [location, inMaintenance]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("user");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const fetchRecentLogs = async () => {
    try {
      console.log("🔄 fetchRecentLogs çağrıldı");
      const logsData = await getRecentActivities();
      if (!Array.isArray(logsData)) {
        console.error("❌ Logs verisi dizi değil:", logsData);
        setRecentLogs([]);
        return;
      }

      const today = new Date();
      const todayString = today.toDateString();
      const todayLogs = logsData.filter((log) => {
        if (!log.access_time) return false;
        try {
          const logDate = new Date(log.access_time);
          const logDateString = logDate.toDateString();
          const twentyFourHoursAgo = new Date(today);
          twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
          return logDate >= twentyFourHoursAgo;
        } catch (e) {
          console.error("❌ Tarih işleme hatası:", e, log);
          return false;
        }
      });

      const sortedLogs = [...todayLogs].sort((a, b) => {
        const dateA = new Date(a.access_time);
        const dateB = new Date(b.access_time);
        return dateB - dateA;
      });

      console.log(`📝 İşlenmiş loglar: ${sortedLogs.length} adet`);
      setRecentLogs(sortedLogs);
    } catch (error) {
      console.error("❌ Loglar yüklenemedi:", error);
      setRecentLogs([]);
      if (error.message.includes("401") || error.message.includes("403")) {
        setError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setError("Loglar yüklenemedi: " + error.message);
      }
    }
  };

  const fetchStats = async () => {
    try {
      console.log("🔄 fetchStats çağrıldı");
      const stats = await getStats();
      setStats(stats);
    } catch (error) {
      console.error("❌ İstatistikler çekilemedi:", error);
      setStats({
        users: 0,
        activeUsers: 0,
        todayEntries: 0,
        todayExits: 0,
        currentlyInOffice: 0,
      });
      if (
        error.message === "Oturum açılmamış" ||
        error.message.includes("Token")
      ) {
        window.location.href = "/login";
      }
    }
  };

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        if (userInfo) {
          const name =
            userInfo.full_name ||
            userInfo.name ||
            userInfo.userName ||
            userInfo.username ||
            userInfo.email ||
            "Yönetici";
          setAdminName(name);
        }

        console.log("🚀 İlk veri çekme başlıyor...");
        await Promise.all([fetchStats(), fetchRecentLogs()]);

        const interval = setInterval(() => {
          console.log("⏰ Otomatik yenileme tetiklendi");
          fetchStats();
          fetchRecentLogs();
        }, 10000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error("❌ Admin veri çekme hatası:", error);
        if (error.message.includes("401") || error.message.includes("403")) {
          setError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.");
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        } else {
          setError("Veriler yüklenemedi: " + error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const handleButtonClick = (view) => {
    setActiveView(view);
    if (!inMaintenance) {
      navigate(`/admin/${view}`);
    }
  };

  const handleUserScheduleClick = (userId) => {
    console.log(
      "AdminPage - handleUserScheduleClick called with userId:",
      userId
    );
    if (inMaintenance) {
      navigate("/maintenance/admin/settings", { state: { userId } });
    } else {
      navigate("/admin/settings", { state: { userId } });
    }
  };

  const getLogUserName = (log) => {
    if (!log) return "Bilinmeyen Kullanıcı";
    if (log.name) {
      const name = log.name || "";
      const surname = log.surname || "";
      const fullName = `${name} ${surname}`.trim();
      return fullName || "Bilinmeyen Kullanıcı";
    }
    if (log.users) {
      const name = log.users.name || "";
      const surname = log.users.surname || "";
      const fullName = `${name} ${surname}`.trim();
      if (fullName) {
        return fullName;
      }
    }
    return "Bilinmeyen Kullanıcı";
  };

  const formatLogTime = (timeString) => {
    if (!timeString) return "--:--";
    try {
      return new Date(timeString).toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "--:--";
    }
  };

  const getLogAction = (accessType) => {
    if (accessType === "IN") return "Giriş Yaptı";
    if (accessType === "OUT") return "Çıkış Yaptı";
    return "Bilinmeyen";
  };

  const getActivityIndicatorClass = (accessType) => {
    if (accessType === "IN") return "activity-green";
    if (accessType === "OUT") return "activity-red";
    return "activity-blue";
  };

  useEffect(() => {
    if (
      error &&
      (error.includes("401") ||
        error.includes("403") ||
        error.includes("Oturum süresi dolmuş"))
    ) {
      const timer = setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  // Eğer maintenance modunda ise, Header ve sidebar'ı gösterme
  if (inMaintenance) {
    return (
      <div className="admin-page">
        <div className={`admin-content ${sidebarOpen ? "sidebar-open" : ""}`}>
          <div className="admin-list">
            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
                {(error.includes("401") ||
                  error.includes("403") ||
                  error.includes("Oturum süresi dolmuş")) && (
                  <span>
                    {" "}
                    (Otomatik olarak giriş sayfasına yönlendirileceksiniz...)
                  </span>
                )}
              </div>
            )}
            <div className="content-area">
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <div className="stats-container">
                        <div className="stat-card primary">
                          <div className="stat-icon">
                            <i className="fas fa-users"></i>
                          </div>
                          <div className="stat-content">
                            <h3>{stats.users}</h3>
                            <p>Toplam Personel</p>
                          </div>
                        </div>
                        <div className="stat-card success">
                          <div className="stat-icon">
                            <i className="fas fa-user-check"></i>
                          </div>
                          <div className="stat-content">
                            <h3>{stats.activeUsers}</h3>
                            <p>Aktif Personel</p>
                          </div>
                        </div>
                        <div className="stat-card warning">
                          <div className="stat-icon">
                            <i className="fas fa-building"></i>
                          </div>
                          <div className="stat-content">
                            <h3>{stats.currentlyInOffice}</h3>
                            <p>Kurumda Çalışan</p>
                          </div>
                        </div>
                        <div className="stat-card danger">
                          <div className="stat-icon">
                            <i className="fas fa-sign-in-alt"></i>
                          </div>
                          <div className="stat-content">
                            <h3>{stats.todayEntries}</h3>
                            <p>Bugün Giriş Yapanlar</p>
                          </div>
                        </div>
                      </div>
                      <ManualAttendanceEntry />
                      <div className="recent-activities">
                        <div className="activities-header">
                          <h3 className="activities-title">
                            <i className="fas fa-chart-line"></i>
                            Anlık Hareketler
                          </h3>
                          <div
                            className="refresh-btn"
                            onClick={fetchRecentLogs}
                          >
                            <i className="fas fa-sync-alt"></i>
                          </div>
                        </div>
                        <div className="activities-list">
                          {recentLogs.length > 0 ? (
                            recentLogs.map((log, index) => (
                              <div
                                key={log.log_id || index}
                                className="activity-item"
                              >
                                <div className="activity-left">
                                  <div
                                    className={`activity-indicator ${getActivityIndicatorClass(
                                      log.access_type
                                    )}`}
                                  ></div>
                                  <div className="activity-content">
                                    <div className="activity-user">
                                      {getLogUserName(log)}
                                    </div>
                                    <div className="activity-action">
                                      {getLogAction(log.access_type)}
                                    </div>
                                  </div>
                                </div>
                                <div className="activity-time">
                                  {formatLogTime(log.access_time)}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="no-logs">
                              <i className="fas fa-inbox no-logs-icon"></i>
                              <p>Henüz hareket kaydı bulunmuyor</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  }
                />
                <Route
                  path="personnel"
                  element={
                    <PersonnelList
                      onUserScheduleClick={handleUserScheduleClick}
                      refreshTrigger={refreshPersonnel}
                    />
                  }
                />
                <Route path="settings" element={<SettingsContent />} />
                <Route path="permissions" element={<PermissionsContent />} />
                <Route path="work-schedule" element={<WorkScheduleContent />} />
                <Route path="attendance/*" element={<Outlet />} />
                <Route path="reports" element={<ReportsContent />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal AdminPage render'i
  return (
    <div className="admin-page">
      <Header
        onLogout={handleLogout}
        userData={userInfo}
        pageType="admin"
        toggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
        onViewPermissions={() => handleButtonClick("permissions")}
      />

      {/* Maintenance Admin için Panel Geçiş Butonu - Sadece normal modda göster */}
      {!inMaintenance && <MaintenancePanelSwitcher />}

      <div className={`admin-content ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="admin-list">
          <div className="list-header">
            <h2>Ana Sayfa</h2>
            <div className="header-welcome">
              <h3>Hoş Geldiniz {adminName} Hocam</h3>
            </div>
          </div>
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
              {(error.includes("401") ||
                error.includes("403") ||
                error.includes("Oturum süresi dolmuş")) && (
                <span>
                  {" "}
                  (Otomatik olarak giriş sayfasına yönlendirileceksiniz...)
                </span>
              )}
            </div>
          )}
          <div className="content-area">
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <div className="stats-container">
                      <div className="stat-card primary">
                        <div className="stat-icon">
                          <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-content">
                          <h3>{stats.users}</h3>
                          <p>Toplam Personel</p>
                        </div>
                      </div>
                      <div className="stat-card success">
                        <div className="stat-icon">
                          <i className="fas fa-user-check"></i>
                        </div>
                        <div className="stat-content">
                          <h3>{stats.activeUsers}</h3>
                          <p>Aktif Personel</p>
                        </div>
                      </div>
                      <div className="stat-card warning">
                        <div className="stat-icon">
                          <i className="fas fa-building"></i>
                        </div>
                        <div className="stat-content">
                          <h3>{stats.currentlyInOffice}</h3>
                          <p>Kurumda Çalışan</p>
                        </div>
                      </div>
                      <div className="stat-card danger">
                        <div className="stat-icon">
                          <i className="fas fa-sign-in-alt"></i>
                        </div>
                        <div className="stat-content">
                          <h3>{stats.todayEntries}</h3>
                          <p>Bugün Giriş Yapanlar</p>
                        </div>
                      </div>
                    </div>
                    <ManualAttendanceEntry />
                    <div className="recent-activities">
                      <div className="activities-header">
                        <h3 className="activities-title">
                          <i className="fas fa-chart-line"></i>
                          Anlık Hareketler
                        </h3>
                        <div className="refresh-btn" onClick={fetchRecentLogs}>
                          <i className="fas fa-sync-alt"></i>
                        </div>
                      </div>
                      <div className="activities-list">
                        {recentLogs.length > 0 ? (
                          recentLogs.map((log, index) => (
                            <div
                              key={log.log_id || index}
                              className="activity-item"
                            >
                              <div className="activity-left">
                                <div
                                  className={`activity-indicator ${getActivityIndicatorClass(
                                    log.access_type
                                  )}`}
                                ></div>
                                <div className="activity-content">
                                  <div className="activity-user">
                                    {getLogUserName(log)}
                                  </div>
                                  <div className="activity-action">
                                    {getLogAction(log.access_type)}
                                  </div>
                                </div>
                              </div>
                              <div className="activity-time">
                                {formatLogTime(log.access_time)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-logs">
                            <i className="fas fa-inbox no-logs-icon"></i>
                            <p>Henüz hareket kaydı bulunmuyor</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                }
              />
              <Route
                path="personnel"
                element={
                  <PersonnelList
                    onUserScheduleClick={handleUserScheduleClick}
                    refreshTrigger={refreshPersonnel}
                  />
                }
              />
              <Route path="settings" element={<SettingsContent />} />
              <Route path="permissions" element={<PermissionsContent />} />
              <Route path="work-schedule" element={<WorkScheduleContent />} />
              <Route path="attendance/*" element={<Outlet />} />
              <Route path="reports" element={<ReportsContent />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
